import React, { PropsWithChildren, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ProfileRelativeLink } from '@/components/navigation';
import { ValidationSummary, ValidatingInput, ValidationForm } from '@/components/forms/validation';
import { checkProfileInviteEmail, inviteProfileMember } from '@/services/profiles';
import { Button, InternalLinkButton } from '@/components/Buttons';
import { useSelector } from 'react-redux';
import { EmailValidity, ProfileMemberPermissions } from '@/types';
import ProfilePermissionsEditor from '@/components/profile/ProfilePermissionsEditor';
import SimpleFormPage from '@/components/SimpleFormPage/SimpleFormPage';
import { IState } from '@/state/IState';
import { Breadcrumbs, isSalutatable, SalutationAndTitleInputs, WithSalutationAndTitle, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { validateEmail } from '@/utils/string';
import theme from './ProfileInvite.module.scss';

const Wrapper: React.FC<PropsWithChildren<{ profileName: string }>> = ({ children, profileName }) => {
    return (
        <>
            <Breadcrumbs>
                <Breadcrumbs.Link to="/profile-users">
                    <Trans>{profileName} users and permissions</Trans>
                </Breadcrumbs.Link>

                <Breadcrumbs.Item>
                    <Trans>Invite a user</Trans>
                </Breadcrumbs.Item>
            </Breadcrumbs>
            <SimpleFormPage header={t`Invite a user`}>{children}</SimpleFormPage>
        </>
    );
};

interface ProfileInviteProps {}

const ProfileInvite: React.FC<ProfileInviteProps> = () => {
    const activeProfile = useSelector((state: IState) => state.global.activeProfile!);
    const profileId = activeProfile.id;

    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const [isEmailValid, setEmailValid] = useState(EmailValidity.NotChecked);

    const [permissions, setPermissions] = useState<ProfileMemberPermissions>({
        profilePermissions: [],
        isAdministrator: false,
    });

    const [salutationAndTitle, setSalutationAndTitle] = useState<WithSalutationAndTitle>({
        inviteeSalutation: null,
        inviteeTitle: null,
    });

    let currentCallId: any = null;

    const onEmailChange = ({ target: { value } }) => {
        const inviteeEmail = value.trim();
        setEmail(inviteeEmail);
        if (!validateEmail(inviteeEmail)) return;

        setEmailValid(EmailValidity.Checking);
        clearTimeout(currentCallId);
        setTimeout(() => {
            checkProfileInviteEmail({
                profileId,
                inviteeEmail,
            })
                .then((x) => {
                    setEmailValid(x);
                })
                .catch((err) => console.error(err));
        }, 500);
    };

    const returnUrl = `/profile-users/`;

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        const invitation = {
            ...permissions,
            ...salutationAndTitle,
            inviteeFirstName: firstName,
            inviteeLastName: lastName,
            inviteeEmail: email,
            inviterProfileId: profileId,
        };

        setSaving(true);

        try {
            await inviteProfileMember(invitation);
            setSuccess(true);
        } catch (e: any) {
            if (e.status === 400) {
                Toast.error(<Trans>Invalid email address.</Trans>);
            } else {
                throw e;
            }
        } finally {
            setSaving(false);
        }
    };

    if (success) {
        return (
            <Wrapper profileName={activeProfile.name}>
                <h3>
                    <Trans>Your invitation has been sent.</Trans>
                </h3>
                <ProfileRelativeLink className="link-unstyled" to={returnUrl}>
                    <Button color="primary">
                        <Trans>Back to profile permissions</Trans>
                    </Button>
                </ProfileRelativeLink>
            </Wrapper>
        );
    }

    return (
        <Wrapper profileName={activeProfile.name}>
            <ValidationForm onSubmit={handleFormSubmit} className="form columns-1">
                {isSalutatable(activeProfile?.countryCode) && (
                    <SalutationAndTitleInputs
                        value={salutationAndTitle}
                        onChange={(change) => setSalutationAndTitle({ ...salutationAndTitle, ...change })}
                    />
                )}

                <label>
                    <Trans>Email</Trans>
                    <ValidatingInput
                        required
                        type="email"
                        autoComplete="off"
                        className="form-control"
                        placeholder={t`Email`}
                        value={email}
                        onChange={onEmailChange}
                    />
                    {isEmailValid === EmailValidity.AlreadyExists && (
                        <div className={theme.error}>
                            <Trans>A user with this email address already exists.</Trans>
                        </div>
                    )}
                </label>
                <label>
                    <Trans>First name</Trans>
                    <ValidatingInput
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        placeholder={t`First name`}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                    />
                </label>
                <label>
                    <Trans>Last name</Trans>
                    <ValidatingInput
                        type="text"
                        autoComplete="off"
                        className="form-control"
                        placeholder={t`Last name`}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                    />
                </label>
                <div className="form-label">
                    <Trans>Permissions</Trans>
                    <div>
                        <ProfilePermissionsEditor value={permissions} onChange={setPermissions} />
                    </div>
                </div>
                <div className="label-offset">
                    <ValidationSummary />
                </div>
                <div className="buttons right">
                    <InternalLinkButton color="tertiary" to={returnUrl}>
                        <Trans>Cancel</Trans>
                    </InternalLinkButton>
                    <Button
                        type="submit"
                        color="primary"
                        loading={saving}
                        disabled={isEmailValid === EmailValidity.AlreadyExists}>
                        <Trans>Invite user</Trans>
                    </Button>
                </div>
            </ValidationForm>
        </Wrapper>
    );
};

export default ProfileInvite;
